import { AlertCircleIcon } from "lucide-react";

import { OptionalTooltip } from "@/components/OptionalTooltip";
import { Row } from "@/components/Row";
import { TableTooltipParagraph } from "@/components/TableTooltipParagraph";
import { Text } from "@/components/Text";
import { Badge } from "@/components/ui/badge";
import { TableCell } from "@/components/ui/table";

import {
  jobStatusToBackgroundColor,
  jobStatusToHoverBackgroundColor,
  jobStatusToLabel,
} from "@/lib/constants";
import { jobs } from "@/lib/jobs";
import { cn } from "@/lib/utils";
import { JobPauseType, JobStatus } from "@/types";

export const TableRoleCell = ({
  companyName,
  jobCity,
  jobPausedAt,
  jobPauseReason,
  jobPauseType,
  jobState,
  jobStatus,
  jobTitle,
  unseenExceptionalCandidaciesCount,
}: {
  companyName: string;
  jobCity?: string;
  jobPausedAt: string | null;
  jobPauseReason: string | null;
  jobPauseType: JobPauseType | null;
  jobState?: string;
  jobStatus: JobStatus;
  jobTitle: string;
  unseenExceptionalCandidaciesCount?: number;
}) => {
  const showPaused = jobs.isDisplayPaused(jobStatus, jobPausedAt);
  // Unseen exceptional candidacies is an indicator on job tracker page that there are exceptional candidacies that have not been reviewed by a human
  const unseenCount = unseenExceptionalCandidaciesCount ?? 0;
  const unseenBadgeColor =
    unseenCount > 9
      ? "bg-red-100 hover:bg-red-100"
      : unseenCount > 4
        ? "bg-orange-100 hover:bg-orange-100"
        : "bg-yellow-100 hover:bg-yellow-100";
  const unseenIconColor =
    unseenCount > 9
      ? "text-red-500"
      : unseenCount > 4
        ? "text-orange-500"
        : "text-yellow-500";
  return (
    <TableCell>
      {unseenCount ? (
        <Badge
          variant="secondary"
          className={cn("ml-[-5px] inline-flex h-5 gap-2", unseenBadgeColor)}
        >
          <AlertCircleIcon className={cn("h-4 w-4", unseenIconColor)} />
          {unseenCount} to review
        </Badge>
      ) : null}
      <TableTooltipParagraph className="font-medium" text={jobTitle} />
      <Row className="gap-2">
        <Row className="min-w-0 gap-1">
          <TableTooltipParagraph className="text-slate-500" text={companyName} />
          {jobCity && jobState ? (
            <Text className="whitespace-nowrap text-sm font-normal text-slate-400">
              ({jobCity}, {jobState})
            </Text>
          ) : null}
        </Row>
        <OptionalTooltip
          text={
            showPaused
              ? jobs.pausedTooltip(jobPausedAt, jobPauseReason, jobPauseType)
              : undefined
          }
          trackingLabel="Paused job tooltip"
        >
          {/* Row wrapper: Badge doesn't forward refs, which the tooltip
              trigger's asChild requires. */}
          <Row>
            <Badge
              className={cn(
                showPaused
                  ? jobs.pauseBackgroundColor(jobPauseType)
                  : jobStatusToBackgroundColor[jobStatus],
                showPaused
                  ? jobs.pauseHoverBackgroundColor(jobPauseType)
                  : jobStatusToHoverBackgroundColor[jobStatus],
                "h-5 text-[10px]",
              )}
              variant="secondary"
            >
              {showPaused
                ? jobs.pauseLabel(jobPauseType)
                : jobStatusToLabel[jobStatus]}
            </Badge>
          </Row>
        </OptionalTooltip>
      </Row>
    </TableCell>
  );
};
